import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { 
  ArrowLeft, Building2, DollarSign,
  Wand2, Copy, CheckCircle2, Loader2, Plus, UserPlus, ChevronRight,
  Play, Pause
} from 'lucide-react';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-blue-100 text-blue-800',
  open: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  closed: 'bg-purple-100 text-purple-800',
  cancelled: 'bg-red-100 text-red-800'
};

const priorityColors = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-green-100 text-green-800'
};

const stageColors = {
  new: 'bg-gray-100 text-gray-800',
  HR_screen: 'bg-blue-100 text-blue-800',
  tech_round_1: 'bg-indigo-100 text-indigo-800',
  tech_round_2: 'bg-violet-100 text-violet-800',
  client_round: 'bg-purple-100 text-purple-800',
  HR_round: 'bg-pink-100 text-pink-800',
  offered: 'bg-amber-100 text-amber-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  joined: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  declined: 'bg-orange-100 text-orange-800',
  withdrawn: 'bg-gray-100 text-gray-800'
};

export default function RequisitionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [requisition, setRequisition] = useState(null);
  const [applications, setApplications] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCandidateDialog, setShowAddCandidateDialog] = useState(false);
  const [showJDDialog, setShowJDDialog] = useState(false);
  const [generatingJD, setGeneratingJD] = useState(false);
  const [generatedJD, setGeneratedJD] = useState({ full_jd: '', social_snippet: '' });
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchRequisition = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/recruitment/requisitions/${id}`);
      setRequisition(response.data);
      
      // Fetch applications for this requisition
      const appsResponse = await api.get('/recruitment/applications', {
        params: { requisition_id: id, limit: 100 }
      });
      setApplications(appsResponse.data.applications || []);
    } catch (error) {
      console.error('Error fetching requisition:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await api.get('/recruitment/candidates', { params: { limit: 100 } });
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  useEffect(() => {
    fetchRequisition();
    fetchCandidates();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await api.post(`/recruitment/requisitions/${id}/status?status=${newStatus}`);
      fetchRequisition();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleAddCandidate = async () => {
    if (!selectedCandidateId) return;
    setSubmitting(true);
    try {
      await api.post('/recruitment/applications', {
        requisition_id: id,
        candidate_id: selectedCandidateId
      });
      setShowAddCandidateDialog(false);
      setSelectedCandidateId('');
      fetchRequisition();
    } catch (error) {
      console.error('Error adding candidate:', error);
      alert(error.response?.data?.detail || 'Failed to add candidate');
    } finally {
      setSubmitting(false);
    }
  };

  const generateJD = async () => {
    setGeneratingJD(true);
    try {
      const response = await api.post('/recruitment/ai/generate-jd', {
        requisition_id: id
      });
      setGeneratedJD({
        full_jd: response.data.full_jd,
        social_snippet: response.data.social_snippet
      });
    } catch (error) {
      console.error('Error generating JD:', error);
      alert('Failed to generate JD');
    } finally {
      setGeneratingJD(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleMoveStage = async (applicationId, newStage) => {
    try {
      await api.put(`/recruitment/applications/${applicationId}/stage`, {
        new_stage: newStage
      });
      fetchRequisition();
    } catch (error) {
      console.error('Error moving stage:', error);
      alert('Failed to update stage');
    }
  };

  if (loading) {
    return <div className="text-center py-16">Loading...</div>;
  }

  if (!requisition) {
    return <div className="text-center py-16">Requisition not found</div>;
  }

  // Group applications by stage
  const applicationsByStage = {};
  applications.forEach(app => {
    const stage = app.current_stage;
    if (!applicationsByStage[stage]) {
      applicationsByStage[stage] = [];
    }
    applicationsByStage[stage].push(app);
  });

  const stages = ['new', 'HR_screen', 'tech_round_1', 'tech_round_2', 'client_round', 'HR_round', 'offered', 'accepted', 'joined'];
  const enabledStages = requisition.enabled_stages || stages;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/recruitment/requisitions')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Requisition Info Card */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CardTitle className="text-2xl">{requisition.title}</CardTitle>
                <Badge className={statusColors[requisition.status]}>
                  {requisition.status.replace('_', ' ')}
                </Badge>
                <Badge className={priorityColors[requisition.priority]}>
                  {requisition.priority}
                </Badge>
              </div>
              <CardDescription className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {requisition.department}
                </span>
                <span>{requisition.location}</span>
                <span>{requisition.experience_min}-{requisition.experience_max} years</span>
                {requisition.budget_min && requisition.budget_max && (
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    ₹{requisition.budget_min}-{requisition.budget_max} LPA
                  </span>
                )}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {requisition.status === 'draft' && (
                <Button onClick={() => handleStatusChange('open')}>
                  <Play className="w-4 h-4 mr-2" />
                  Open Position
                </Button>
              )}
              {requisition.status === 'open' && (
                <>
                  <Button variant="outline" onClick={() => handleStatusChange('on_hold')}>
                    <Pause className="w-4 h-4 mr-2" />
                    Put on Hold
                  </Button>
                  <Button variant="outline" onClick={() => handleStatusChange('closed')}>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Close
                  </Button>
                </>
              )}
              {requisition.status === 'on_hold' && (
                <Button onClick={() => handleStatusChange('open')}>
                  <Play className="w-4 h-4 mr-2" />
                  Resume
                </Button>
              )}
              <Dialog open={showJDDialog} onOpenChange={setShowJDDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate JD
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>AI-Generated Job Description</DialogTitle>
                    <DialogDescription>LinkedIn-optimized JD powered by Gemini AI</DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    {!generatedJD.full_jd ? (
                      <div className="text-center py-8">
                        <Button onClick={generateJD} disabled={generatingJD}>
                          {generatingJD ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                          ) : (
                            <><Wand2 className="w-4 h-4 mr-2" /> Generate JD</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <Tabs defaultValue="full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="full">Full JD</TabsTrigger>
                          <TabsTrigger value="snippet">Social Snippet</TabsTrigger>
                        </TabsList>
                        <TabsContent value="full" className="relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(generatedJD.full_jd)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Textarea
                            value={generatedJD.full_jd}
                            readOnly
                            rows={20}
                            className="font-mono text-sm"
                          />
                        </TabsContent>
                        <TabsContent value="snippet" className="relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(generatedJD.social_snippet)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Textarea
                            value={generatedJD.social_snippet}
                            readOnly
                            rows={5}
                          />
                        </TabsContent>
                      </Tabs>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{requisition.total_candidates || 0}</p>
              <p className="text-sm text-gray-500">Total Candidates</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{requisition.candidates_in_pipeline || 0}</p>
              <p className="text-sm text-gray-500">In Pipeline</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">{requisition.positions_filled}/{requisition.number_of_positions}</p>
              <p className="text-sm text-gray-500">Positions Filled</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-600">
                {requisition.target_closure_date ? new Date(requisition.target_closure_date).toLocaleDateString() : 'N/A'}
              </p>
              <p className="text-sm text-gray-500">Target Date</p>
            </div>
          </div>
          
          {/* Skills */}
          <div className="mb-4">
            <h4 className="font-medium mb-2">Required Skills</h4>
            <div className="flex flex-wrap gap-2">
              {requisition.skills_required?.map((skill, idx) => (
                <Badge key={idx} variant="default">{skill}</Badge>
              ))}
            </div>
          </div>
          {requisition.skills_preferred?.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Preferred Skills</h4>
              <div className="flex flex-wrap gap-2">
                {requisition.skills_preferred.map((skill, idx) => (
                  <Badge key={idx} variant="outline">{skill}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Candidate Pipeline */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Candidate Pipeline</CardTitle>
            <Dialog open={showAddCandidateDialog} onOpenChange={setShowAddCandidateDialog}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Candidate
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Candidate to Pipeline</DialogTitle>
                  <DialogDescription>Select an existing candidate or create new</DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-4">
                  <div>
                    <Label>Select Candidate</Label>
                    <Select value={selectedCandidateId} onValueChange={setSelectedCandidateId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a candidate" />
                      </SelectTrigger>
                      <SelectContent>
                        {candidates.map((cand) => (
                          <SelectItem key={cand.id} value={cand.id}>
                            {cand.name} - {cand.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setShowAddCandidateDialog(false);
                      navigate('/recruitment/candidates');
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Candidate
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddCandidateDialog(false)}>Cancel</Button>
                  <Button onClick={handleAddCandidate} disabled={!selectedCandidateId || submitting}>
                    {submitting ? 'Adding...' : 'Add to Pipeline'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {applications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No candidates in pipeline yet. Add your first candidate to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex gap-4 min-w-max pb-4">
                {enabledStages.filter(s => !['accepted', 'declined', 'rejected', 'withdrawn'].includes(s)).map((stage) => (
                  <div key={stage} className="w-64 flex-shrink-0">
                    <div className={`p-2 rounded-t-lg ${stageColors[stage]} font-medium text-center`}>
                      {stage.replace('_', ' ')} ({applicationsByStage[stage]?.length || 0})
                    </div>
                    <div className="border border-t-0 rounded-b-lg p-2 min-h-[200px] bg-gray-50 space-y-2">
                      {applicationsByStage[stage]?.map((app) => (
                        <div
                          key={app.id}
                          className="bg-white p-3 rounded border shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => navigate(`/recruitment/candidates/${app.candidate_id}`)}
                        >
                          <p className="font-medium text-sm">{app.candidate_name}</p>
                          <p className="text-xs text-gray-500">{app.candidate_email}</p>
                          <div className="flex justify-between items-center mt-2">
                            <Badge variant="outline" className="text-xs">
                              {app.total_interviews || 0} interviews
                            </Badge>
                            {stage !== 'joined' && stage !== 'offered' && (
                              <Select
                                value=""
                                onValueChange={(newStage) => handleMoveStage(app.id, newStage)}
                              >
                                <SelectTrigger className="w-20 h-6 text-xs">
                                  <ChevronRight className="w-3 h-3" />
                                </SelectTrigger>
                                <SelectContent>
                                  {enabledStages
                                    .filter(s => s !== stage && !['accepted', 'declined', 'withdrawn'].includes(s))
                                    .map(s => (
                                      <SelectItem key={s} value={s} className="text-xs">
                                        {s.replace('_', ' ')}
                                      </SelectItem>
                                    ))
                                  }
                                  <SelectItem value="rejected" className="text-xs text-red-600">Reject</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stage Breakdown */}
      {requisition.stage_breakdown && Object.keys(requisition.stage_breakdown).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Stage Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(requisition.stage_breakdown).map(([stage, count]) => (
                <div key={stage} className={`p-3 rounded-lg ${stageColors[stage]}`}>
                  <p className="font-bold text-lg">{count}</p>
                  <p className="text-sm">{stage.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
